import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Select from '@/components/Select/Select';
import Markets from '@/constants/Markets';
import { PlatformProviders } from '@/constants/PlatformProviders';
import {
  fetchFilters,
  fetchPartners,
  postPartnersLead,
  PostPartnersLeadParams,
} from '@/lib/api';
import {
  mapConfigToOptions,
  mapConstantsToOptions,
} from '@/lib/listOptions';
import { styled } from '@mui/material/styles';
import TextField from '@/components/TextField/TextField';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import { AxiosError } from 'axios';
import { ApiError } from '@/lib/types';
import useApiErrorHandler from '@/hooks/useApiErrorHandler';

const CreatePartnerLeadModal = ({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}) => {
  const { data, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const { data: dataPlatforms, isLoading: isLoadingPlatforms } =
    useQuery({
      queryKey: ['platforms'],
      queryFn: () => fetchPartners({ type: ['platform'] }),
    });
  const [partnerName, setPartnerName] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState<number[]>(
    [],
  );
  const [selectedPlatformProvider, setSelectedPlatformProvider] =
    useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<
    number | null
  >(null);
  const { handleError } = useApiErrorHandler();
  const [formErrors, setFormErrors] = useState<{
    [key: string]: string;
  }>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const marketItems = mapConfigToOptions(Markets, data?.markets, {
    id: 'market_id',
    name: 'market_name',
  });
  const platformProviderItems =
    mapConstantsToOptions(PlatformProviders);
  const platformItems = dataPlatforms?.map((item) => ({
    id: item.id,
    label: item.name,
    value: item.id,
  }));

  const handleApiError = (error: AxiosError<ApiError>) => {
    const { generalErrors, fieldErrors, unhandledErrors } =
      handleError(error);
    console.log(generalErrors, fieldErrors, unhandledErrors);
    setFormErrors(fieldErrors);
    setGeneralErrors([...generalErrors, ...unhandledErrors]);
  };

  const { mutate, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostPartnersLeadParams
  >({
    mutationFn: postPartnersLead,
    onSuccess: () => {
      onSave();
    },
    onError: handleApiError,
  });

  useEffect(() => {
    if (!open) {
      setPartnerName('');
      setSelectedMarkets([]);
      setSelectedPlatformProvider(null);
      setSelectedPlatform(null);
      setFormErrors({});
      setGeneralErrors([]);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (!partnerName) {
      setFormErrors({ name: 'Partner Name is required' });
      return;
    }
    setFormErrors({});
    setGeneralErrors([]);

    mutate({
      name: partnerName,
      type: 'lead',
      selectedMarkets,
      selectedPlatformProvider,
      parent_id: selectedPlatform,
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Partner Lead"
      generalErrors={generalErrors}
      actions={[
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          disabled={isPending}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="save"
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          loading={isPending}
          aria-label="Save Button"
        >
          <SaveIcon />
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>,
      ]}
    >
      <Label>
        <FormattedMessage
          id="partnerName"
          defaultMessage="Partner Name"
        />
        <Asterix>*</Asterix>
      </Label>
      <StyledTextField
        value={partnerName}
        onChange={(e) => {
          setPartnerName(e.target.value);
        }}
        placeholder="Type Partner Name"
        variant="outlined"
        fullWidth
        error={!!formErrors.name}
        helperText={formErrors.name || ' '}
      />
      <Label>
        <FormattedMessage
          id="selectMarkets"
          defaultMessage="Select Markets you would like to add"
        />
      </Label>
      <Select
        multiple
        disabled={isLoadingFilters}
        placeholder={
          isLoadingFilters ? (
            <FormattedMessage
              id="loading"
              defaultMessage="Loading..."
            />
          ) : (
            <FormattedMessage id="select" defaultMessage="Select" />
          )
        }
        options={isLoadingFilters ? [] : marketItems}
        value={selectedMarkets}
        onChange={(v) => setSelectedMarkets(v as number[])}
        aria-label="Markets"
        variant="outlined"
        searchEnabled
        size="small"
      />
      <Label topMargin>
        <FormattedMessage
          id="platformProvider"
          defaultMessage="Platform Provider"
        />
      </Label>
      <Select
        placeholder={
          <FormattedMessage id="select" defaultMessage="Select" />
        }
        options={platformProviderItems}
        aria-label="Platform Provider"
        value={selectedPlatformProvider}
        onChange={(v) =>
          setSelectedPlatformProvider(v as string | null)
        }
        variant="outlined"
      />
      <Label topMargin>
        <FormattedMessage
          id="selectPlatform"
          defaultMessage="Select Platform"
        />
      </Label>
      <Select
        disabled={isLoadingPlatforms}
        placeholder={
          isLoadingPlatforms ? (
            <FormattedMessage
              id="loading"
              defaultMessage="Loading..."
            />
          ) : (
            <FormattedMessage id="select" defaultMessage="Select" />
          )
        }
        options={isLoadingPlatforms ? [] : platformItems || []}
        value={selectedPlatform}
        onChange={(v) => setSelectedPlatform(v as number | null)}
        aria-label="Platform"
        variant="outlined"
        searchEnabled
      />
    </Modal>
  );
};

const Label = styled('div')<{ topMargin?: boolean }>(
  ({ theme, topMargin = false }) => ({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    marginBottom: theme.spacing(1),
    marginTop: topMargin ? theme.spacing(4) : 0,
  }),
);

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const StyledTextField = styled(TextField)({
  marginTop: 0,
  marginBottom: 11,
});

export default CreatePartnerLeadModal;
